import React, { FC, useState } from "react";
import GallerySlider from "components/GallerySlider/GallerySlider";
import { StayDataType } from "data/types";
import StartRating from "components/StartRating/StartRating";
import { updatePropertiesListingStatus } from "../../redux/reducers/Properties/PropertiesSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "redux/store";
import successHandler from "utils/helpers/SuccessHandler";
import { currencySymbol } from "shared/CurrencySymbol";
import { MdLocationPin } from "react-icons/md";

export interface StayCardV5Props {
  className?: string;
  data?: StayDataType;
  size?: "default" | "small";
  setAllData: React.Dispatch<React.SetStateAction<StayDataType[]>>;
  stayListings: StayDataType[];
}

const StayCardV5: FC<StayCardV5Props> = ({
  size = "default",
  className = "",
  data,
  setAllData,
  stayListings,
}) => {
  // ✅ Hooks always before any early return (rules-of-hooks)
  const dispatch = useDispatch<AppDispatch>();
  const [activeLoading, setActiveLoading] = useState(false);

  if (!data) return null;

  const {
    place_images,
    address,
    title,
    href,
    price,
    reviewStart,
    reviewCount,
    id,
    property_state,
    active,
    property_category,
    property_type,
    slug,
    reviewTotal,
    totalBedrooms,
  } = data;

  // ✅ Guard: never pass undefined/empty to GallerySlider — prevents demo image bleed
  const safeImages =
    Array.isArray(place_images) && place_images.length > 0 ? place_images : [];

  const handleStatusUpdate = async (status: boolean, itemId: any) => {
    setActiveLoading(true);
    const payload = { property: { active: !status, id: itemId } };
    const action = await dispatch(updatePropertiesListingStatus(payload));
    if (action.payload.success) {
      const modifiedArr = stayListings?.map((item: any) =>
        item.id === itemId ? { ...item, active: !status } : item
      );
      setAllData(modifiedArr);
    }
    successHandler(
      active ? "The property has been paused!" : "The property has been activated!"
    );
    setActiveLoading(false);
  };

  const handleToggleListingStatus = () => {
    if (active !== undefined) handleStatusUpdate(active, id);
  };

  const getStatusConfig = (status: string | undefined) => {
    switch (status) {
      case "waiting_for_approval":
        return { label: "Pending Approval", className: "bg-amber-500 text-white" };
      case "approved":
        return { label: "Approved", className: "bg-[#1A1A2E] text-white" };
      default:
        return null;
    }
  };

  const statusConfig = getStatusConfig(property_state);

  const renderSliderGallery = () => (
    <div className="relative w-full">
      <GallerySlider
        uniqueID={`StayCard_${id}`}
        ratioClass="aspect-w-4 aspect-h-3"
        galleryImgs={safeImages}
        href={href}
      />

      {/* Approval status — top left */}
      {statusConfig && (
        <div className={`absolute left-3 top-3 z-[1] flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold shadow-lg ${statusConfig.className}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-white/60" />
          {statusConfig.label}
        </div>
      )}

      {/* Active / Paused — top right */}
      <div className={`absolute right-3 top-3 z-[1] flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold shadow-lg ${
        active ? "bg-white text-[#1A1A2E]" : "bg-[#1A1A2E]/80 text-white backdrop-blur-sm"
      }`}>
        <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-[#F56948]" : "bg-neutral-400"}`} />
        {active ? "Active" : "Paused"}
      </div>
    </div>
  );

  const renderContent = () => (
    <div className="p-4 flex flex-col gap-3">

      {/* Title */}
      <h3 className="text-sm font-bold text-[#1A1A2E] dark:text-white line-clamp-2 leading-snug">
        {title}
      </h3>

      {/* Address */}
      <div className="flex items-start gap-1.5">
        <MdLocationPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#F56948]" />
        <span className="text-xs text-neutral-400 line-clamp-1">
          {address || "Address not provided"}
        </span>
      </div>

      {/* Divider */}
      <div className="h-px bg-neutral-100 dark:bg-neutral-800" />

      {/* Meta chips */}
      <div className="flex flex-wrap gap-1.5">
        {property_type && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#F56948]/10 text-[#F56948] border border-[#F56948]/20">
            {property_type}
          </span>
        )}
        {property_category && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#1A1A2E]/8 text-[#1A1A2E] dark:bg-white/10 dark:text-neutral-300 border border-[#1A1A2E]/10 dark:border-white/10">
            {property_category}
          </span>
        )}
        {totalBedrooms && (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 10V6a1 1 0 011-1h16a1 1 0 011 1v4M3 10h18M3 10v8m18-8v8M3 18h18" />
            </svg>
            {totalBedrooms} {Number(totalBedrooms) === 1 ? "Bed" : "Beds"}
          </span>
        )}
      </div>

      {/* Price + Rating */}
      <div className="flex items-end justify-between">
        <div className="flex items-baseline gap-1">
          <span className="text-lg font-extrabold text-[#1A1A2E] dark:text-white tracking-tight">
            {currencySymbol()}{Number(price)?.toFixed(2) ?? "N/A"}
          </span>
          {size === "default" && (
            <span className="text-xs text-neutral-400 font-normal">/mo</span>
          )}
        </div>
        {!!reviewStart && (
          <StartRating reviewCount={reviewCount} reviewTotal={reviewTotal} />
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-neutral-100 dark:bg-neutral-800" />

      {/* Actions */}
      <div className="grid grid-cols-3 gap-2">

        {/* Edit */}
        <a
          href={`/add-listing-1/${slug}`}
          title="Edit Listing"
          className="flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl text-xs font-semibold border-2 border-neutral-200 dark:border-neutral-700 text-[#1A1A2E] dark:text-neutral-300 hover:border-[#F56948]/40 hover:text-[#F56948] transition-all duration-150"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit
        </a>

        {/* View */}
        <a
          href={`/listing-stay-detail/${slug}`}
          title="View Listing"
          className="flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl text-xs font-semibold bg-[#1A1A2E] dark:bg-neutral-700 text-white hover:bg-[#2c2c45] dark:hover:bg-neutral-600 transition-all duration-150 shadow-sm"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          View
        </a>

        {/* Pause / Resume */}
        <button
          title={active ? "Pause Listing" : "Resume Listing"}
          onClick={handleToggleListingStatus}
          disabled={activeLoading}
          className={`flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl text-xs font-semibold transition-all duration-150 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed ${
            active
              ? "bg-[#F56948] hover:bg-[#e0522f] text-white"
              : "bg-emerald-500 hover:bg-emerald-600 text-white"
          }`}
        >
          {activeLoading ? (
            <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : active ? (
            <>
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 4H6v16h4V4zm8 0h-4v16h4V4z" />
              </svg>
              Pause
            </>
          ) : (
            <>
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
              </svg>
              Resume
            </>
          )}
        </button>

      </div>
    </div>
  );

  return (
    <div
      className={`nc-StayCard group relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden will-change-transform hover:shadow-[0_8px_30px_rgba(245,105,72,0.12)] hover:-translate-y-0.5 transition-all duration-200 ${className}`}
      data-nc-id="StayCard"
    >
      {renderSliderGallery()}
      {renderContent()}
    </div>
  );
};

export default StayCardV5;